import { IFeedLiveFilters, IFeedLeague, IFeedTeam } from '@/types';
import { FeedLiveItem } from '@/models/feedLive';
import { EventFeedType, EventPeriod, Line, StoreName } from '@/constants';
import { useSearchedTeamIds } from '@/stores/composables/feed/useSearchedTeamIds';
import { useSearchedLeagueIds } from '@/stores/composables/feed/useSearchedLeagueIds';
import { useFilteredLeagueIds } from '@/stores/composables/feed/useFilteredLeagueIds';
import { useBetsChangedHandler } from '@/stores/composables/feed/useBetsChangedHandler';
import { useFeedResponseProcessor } from '@/stores/composables/feed/useFeedResponseProcessor';
import { usePersistentFilters } from '@/stores/composables/usePersistentFilters';

export const useFeedLiveStore = defineStore(StoreName.FeedLive, () => {
    const liveItems = shallowRef<FeedLiveItem[]>([]);
    const liveVersion = ref<number>(0);
    const liveEventsPositionIdx = ref<Record<string, number>>({});
    const total = ref<number>(0);
    const loading = ref<boolean>(false);
    const isExtraFiltersDisplayed = ref<boolean>(false);
    const customColumns = ref<string[] | null>(null);
    const filters = reactive<IFeedLiveFilters>({
        search: '',
        line: Line.Main,
        customLeagueId: null,
        eventFeedTypeId: EventFeedType.Live,
        leagueGroupId: null,
        periodTypeId: EventPeriod.FullTime,
        customEventId: null,
        isPositionOnly: false,
    });

    usePersistentFilters(filters, ['isPositionOnly']);

    const leagueItems = ref<Record<number, IFeedLeague>>({});
    const teamItems = ref<Record<number, IFeedTeam>>({});

    function reset() {
        liveItems.value = [];
        liveVersion.value = 0;
        liveEventsPositionIdx.value = {};
        leagueItems.value = {};
        teamItems.value = {};
    }

    const { checkResponse: checkLiveResponse, processResponse: processLiveResponse } = useFeedResponseProcessor<FeedLiveItem>(
        EventFeedType.Live,
        liveItems,
        filters,
        'FeedLiveItem',
        liveEventsPositionIdx,
        liveVersion,
        leagueItems,
        teamItems
    );

    const searchRef = toRef(filters, 'search');
    const { searchedTeamIds } = useSearchedTeamIds(searchRef, teamItems);
    const { searchedLeagueIds } = useSearchedLeagueIds(searchRef, leagueItems);
    const { filteredLeagueIds } = useFilteredLeagueIds(filters);

    useBetsChangedHandler(liveItems);

    const displayedItems = shallowRef<FeedLiveItem[]>([]);
    const displayedItemsLength = ref<number>(0);

    //we use watch because need to exclude line, customEventId from update initiators
    watch(
        [
            liveItems,
            filteredLeagueIds,
            //() => filters.customEventId,
            () => filters.isPositionOnly,
            () => filters.search,
            searchedLeagueIds,
            searchedTeamIds,
            //() => filters.line // no need run update if line changed. If player changes line filter, will be new events, only after that we should update displayedItems
        ],
        () => {
            console.log('[watch] liveDisplayedItems. Total events count: ', liveItems.value.length);

            let groupsCount = 0;
            const out: FeedLiveItem[] = [];
            let lastLeagueId = 0;
            for (let i = 0; i < liveItems.value.length; i++) {
                const item = liveItems.value[i];

                const pass =
                    //we should filter by line, because event can have all lines(for betting popup for example)
                    (filters.line >= item.line.value || (filters.customEventId !== null && filters.customEventId === item.customEventId.value)) &&
                    (filteredLeagueIds.value === null || filteredLeagueIds.value.includes(item.leagueId.value)) &&
                    (!filters.isPositionOnly || item.position.value !== null) &&
                    (!filters.search ||
                        searchedLeagueIds.value!.includes(item.leagueId.value) ||
                        searchedTeamIds.value!.includes(item.teamHomeId.value) ||
                        searchedTeamIds.value!.includes(item.teamAwayId.value));

                if (!pass) continue;

                if (lastLeagueId !== item.leagueId.value) {
                    item.isNewGroupStarted.value = 1;
                    lastLeagueId = item.leagueId.value;
                    groupsCount++;
                } else {
                    item.isNewGroupStarted.value = 0;
                }

                out.push(item);
            }

            displayedItems.value = out;
            displayedItemsLength.value = displayedItems.value.length + groupsCount;

            console.time(`Render live ${displayedItems.value.length} items`);
            nextTick().then(() => console.timeEnd(`Render live ${displayedItems.value.length} items`));
        }
    );

    /*const displayedItems = computed<FeedLiveItem[]>(() => {
        console.log('[computed] liveDisplayedItems. Total events count: ', liveItems.value.length);

        const filteredItems = liveItems.value.filter((item) => {
            return (
                (filters.line! >= item.line.value || (filters.customEventId !== null && filters.customEventId === item.customEventId.value)) &&
                (filteredLeagueIds.value === null || filteredLeagueIds.value.includes(item.leagueId.value)) &&
                (!filters.isPositionOnly || item.position.value !== null) &&
                (!filters.search ||
                    searchedLeagueIds.value!.includes(item.leagueId.value) ||
                    searchedTeamIds.value!.includes(item.teamHomeId.value) ||
                    searchedTeamIds.value!.includes(item.teamAwayId.value))
            );
        }) as FeedLiveItem[];

        console.time(`Render live ${filteredItems.length} items`);
        nextTick().then(() => console.timeEnd(`Render live ${filteredItems.length} items`));
        return filteredItems;
    });*/

    return {
        checkLiveResponse,
        customColumns,
        displayedItems,
        displayedItemsLength,
        filters,
        isExtraFiltersDisplayed,
        leagueItems,
        liveEventsPositionIdx,
        liveItems,
        liveVersion,
        loading,
        processLiveResponse,
        reset,
        teamItems,
        total,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFeedLiveStore, import.meta.hot));
}
